/**
 * The attestation dialogs (plan docs/plans/lms-plan.md 7.3, R20, D8; WP-40).
 *
 * Every completion carries an attestation. The statement is fixed per kind:
 * the frontend shows it and the backend stores the text it used (WP-34
 * attestation.js holds the same sentences). The learner types nothing but
 * clicks Attest; the name and email are taken from the session's user row
 * server-side, so the dialog only shows who is signing.
 *
 *   AttestDialog  the quiz path (D8: no credential re-check)
 *   ReauthDialog  read_ack, the SOP and acknowledge path (7.3: the password
 *                 re-check). The password lives in this component's state
 *                 only while the dialog is open, is sent once per click, is
 *                 cleared after every try and on close, and is never logged
 *                 or stored (G14). A 401 LMS_REAUTH_FAILED is shown in the
 *                 dialog and nothing is signed.
 */
import { useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { KeyRound, ShieldCheck } from 'lucide-react';
import Modal from '../../../ui/Modal';
import { fieldCls, primaryBtnCls, secondaryBtnCls } from '../../../ui/styles';
import { noticeCls } from './styles';

/** 7.3: the statements, verbatim (the backend stores the same text). */
export const ATTESTATION_STATEMENTS = {
  quiz: 'I completed this training myself, without assistance, and I understand the material.',
  read_ack: 'I have read and understand this document in full and will follow it in my work.',
} as const;

function Signer({ name, email }: { name: string | null; email: string | null }) {
  if (!name && !email) return null;
  return (
    <p className="text-xs text-gray-500 dark:text-gray-400" data-testid="lms-attest-signer">
      {`Signing as ${name || email}${name && email ? ` (${email})` : ''}`}
    </p>
  );
}

function Statement({ text }: { text: string }) {
  return (
    <blockquote
      className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-900 dark:border-gray-700 dark:bg-gray-900/40 dark:text-gray-100"
      data-testid="lms-attest-statement"
    >
      {text}
    </blockquote>
  );
}

export interface AttestDialogProps {
  open: boolean;
  signerName: string | null;
  signerEmail: string | null;
  busy: boolean;
  error: string | null;
  onAttest: () => void;
  onClose: () => void;
}

export function AttestDialog({ open, signerName, signerEmail, busy, error, onAttest, onClose }: AttestDialogProps) {
  return (
    <Modal isOpen={open} onClose={busy ? () => undefined : onClose} title="Attest and finish" maxWidth="max-w-lg">
      <div className="space-y-4" data-testid="lms-attest-dialog">
        <p className="text-sm text-gray-700 dark:text-gray-300">
          Your completion is recorded with this statement, your name and the time.
        </p>
        <Statement text={ATTESTATION_STATEMENTS.quiz} />
        <Signer name={signerName} email={signerEmail} />
        <div aria-live="assertive">
          {error ? (
            <div className={noticeCls('red')} role="alert" data-testid="lms-attest-error">
              {error}
            </div>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          <button type="button" className={secondaryBtnCls} onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="button" className={primaryBtnCls} onClick={onAttest} disabled={busy} data-testid="lms-attest-confirm">
            <ShieldCheck className="h-4 w-4" aria-hidden="true" />
            {busy ? 'Recording...' : 'Attest'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

export interface ReauthDialogProps {
  open: boolean;
  title: string;
  signerName: string | null;
  signerEmail: string | null;
  /** Extra line above the statement (for example the change being acknowledged). */
  intro?: string | null;
  /**
   * Post the password once; resolve with an error message to show (the
   * dialog stays open) or null on success (the parent closes it).
   */
  onSign: (password: string) => Promise<string | null>;
  onClose: () => void;
}

export function ReauthDialog({ open, title, signerName, signerEmail, intro, onSign, onClose }: ReauthDialogProps) {
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // A second click before the first answer lands must not post the password again.
  const inFlight = useRef(false);

  useEffect(() => {
    if (!open) {
      setPassword('');
      setError(null);
      setBusy(false);
      inFlight.current = false;
    }
  }, [open]);

  const submit = async (e?: FormEvent) => {
    if (e) e.preventDefault();
    if (inFlight.current || !password) return;
    inFlight.current = true;
    setBusy(true);
    setError(null);
    const typed = password;
    // G14: the field is emptied at once; the typed value lives only in this call.
    setPassword('');
    let message: string | null;
    try {
      message = await onSign(typed);
    } catch {
      message = 'Your acknowledgement could not be recorded. Try again.';
    }
    inFlight.current = false;
    setBusy(false);
    if (message) setError(message);
  };

  return (
    <Modal isOpen={open} onClose={busy ? () => undefined : onClose} title={title} maxWidth="max-w-lg">
      <form className="space-y-4" onSubmit={submit} data-testid="lms-reauth-dialog" autoComplete="off">
        {intro ? <p className="text-sm text-gray-700 dark:text-gray-300">{intro}</p> : null}
        <Statement text={ATTESTATION_STATEMENTS.read_ack} />
        <Signer name={signerName} email={signerEmail} />
        <div className="space-y-1">
          <label htmlFor="lms-reauth-password" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Your password
          </label>
          <input
            id="lms-reauth-password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={busy}
            className={`${fieldCls} min-h-[44px] w-full sm:min-h-0`}
            data-testid="lms-reauth-password"
          />
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Signing a document needs your password again (an electronic signature). It is checked once and never stored.
          </p>
        </div>
        <div aria-live="assertive">
          {error ? (
            <div className={noticeCls('red')} role="alert" data-testid="lms-reauth-error">
              {error}
            </div>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          <button type="button" className={secondaryBtnCls} onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" className={primaryBtnCls} disabled={busy || !password} data-testid="lms-reauth-submit">
            <KeyRound className="h-4 w-4" aria-hidden="true" />
            {busy ? 'Checking...' : 'Sign and acknowledge'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export default AttestDialog;
